import * as React from 'react';

import { enumCameraStatus } from '@rematter/frontend-lib-api-client';
import { Button, ConfirmPopoverButton } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue } from 'jotai';
import { LucidePlay, Trash2 } from 'lucide-react';

import { $remoteCameraDetail } from '../../../_state/cameraDetail.state';
import { useTranslations } from '../../../i18n/useTranslations';
import type { CameraDetailRecord } from '../../../types/sidecar';

import { useCameraLocalClient } from '~/shared/providers/libp2p/useCameraLocalClient';
import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';

type Props = {
  camera: CameraDetailRecord;
  onDelete: () => void | Promise<void>;
  onPhaseChange: (phase: string | null) => void;
  refetch?: () => void;
};

export const CameraHeaderActions: React.FC<Props> = ({
  camera,
  onDelete,
  onPhaseChange,
  refetch,
}) => {
  const t = useTranslations();
  const toast = useToast();
  const { refetch: refetchCameraDetail } = useAtomValue($remoteCameraDetail);
  const [isInitializing, setIsInitializing] = React.useState(false);
  const [sidecarPhase, setSidecarPhase] = React.useState<string | null>(null);

  const isRtsp = camera.driverType === 'rtsp';
  const cameraClient = useCameraLocalClient(
    isRtsp ? '' : (camera.sidecarId ?? ''),
  );
  const { connection } = useHardwareSidecarConnection({
    sidecar: camera.sidecarDetail,
    onConnectionFailed: refetchCameraDetail,
  });

  React.useEffect(() => {
    if (isRtsp || !cameraClient || !camera) {
      setSidecarPhase(null);
      return;
    }

    let cancelled = false;

    cameraClient
      .getCameraInfo({ cameraId: camera.id })
      .then((res) => {
        if (!cancelled) {
          setSidecarPhase(res.camera?.phase ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSidecarPhase(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cameraClient, camera, isRtsp]);

  React.useEffect(() => {
    onPhaseChange(sidecarPhase);
  }, [sidecarPhase, onPhaseChange]);

  const handleInitialize = React.useCallback(async () => {
    if (!cameraClient || !camera) return;

    try {
      setIsInitializing(true);
      const response = await cameraClient.initializeCamera({
        cameraId: camera.id,
      });
      if (!response.success) {
        throw new Error(response.errorMessage || 'Initialization failed');
      }
      toast.success(t.hardwareView.cameraDetailView.messages.initializeSuccess);
      refetch?.();
    } catch (err) {
      toast.error(
        t.hardwareView.cameraDetailView.messages.initializeError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
    } finally {
      setIsInitializing(false);
    }
  }, [cameraClient, camera, toast, t, refetch]);

  const isConnected = connection?.connected ?? false;
  const showInitialize =
    !isRtsp &&
    (sidecarPhase === 'discovered' ||
      (!sidecarPhase && camera.status === enumCameraStatus.Discovered));

  return (
    <div className="@CameraHeaderActions flex items-center gap-2">
      {showInitialize && (
        <Button
          icon={<LucidePlay className="size-4" />}
          onPress={handleInitialize}
          isDisabled={isInitializing || !isConnected || !cameraClient}
        >
          {isInitializing
            ? t.hardwareView.cameraDetailView.actions.initializing
            : t.hardwareView.cameraDetailView.actions.initialize}
        </Button>
      )}
      <ConfirmPopoverButton
        onConfirm={onDelete}
        message={t.hardwareView.cameraDetailView.messages.deleteConfirmation}
        confirmLabel={t.hardwareView.common.confirmDelete}
        cancelLabel={t.hardwareView.common.cancel}
        buttonProps={{
          variant: 'outline',
          color: 'failure',
          icon: <Trash2 className="size-4" />,
        }}
      >
        {t.hardwareView.cameraDetailView.actions.delete}
      </ConfirmPopoverButton>
    </div>
  );
};
